import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useSyncWatch } from "../../context/SyncContext";
import { toast } from "sonner";

/**
 * useWeekData — encapsulates the data fetching for a Planificator week.
 *
 * Returns:
 *   { data, projects, regii, company, holidays, logoDataUrl, loading, reload }
 *
 * Side effects:
 *   - Re-fetches whenever `weekStart` changes (or `weekEnd` for cross-year ranges).
 *   - Subscribes to sync events (schedule/projects/regii/vacations/persons) → auto-reload.
 *
 * Notes:
 *   - data.entries, data.persons, data.cars come from /planificator/week (already enriched by backend).
 *   - logoDataUrl: prefers uploaded company.logo_file_id, falls back to /logo-convic.jpg.
 */
export default function useWeekData(weekStart, weekEnd) {
  const [data, setData] = useState({ entries: [], persons: [], cars: [] });
  const [projects, setProjects] = useState([]);
  const [regii, setRegii] = useState([]);
  const [company, setCompany] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!weekStart || !weekEnd) return;
    setLoading(true);
    try {
      const yStart = parseInt(weekStart.slice(0, 4), 10);
      const yEnd = parseInt(weekEnd.slice(0, 4), 10);
      const [{ data: weekResp }, pj, rg, co, h1, h2] = await Promise.all([
        api.get(`/planificator/week?start=${weekStart}&end=${weekEnd}`),
        api.get("/projects").catch(() => ({ data: [] })),
        api.get("/regii").catch(() => ({ data: [] })),
        api.get("/company").catch(() => ({ data: {} })),
        api.get(`/holidays?year=${yStart}`).catch(() => ({ data: [] })),
        yEnd !== yStart ? api.get(`/holidays?year=${yEnd}`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setData(weekResp);
      setProjects(pj.data || []);
      setRegii(rg.data || []);
      setCompany(co.data || {});
      setHolidays([...(h1.data || []), ...(h2.data || [])]);
      // Logo: try uploaded, else fallback to bundled
      if (co.data?.logo_file_id) {
        try {
          const res = await api.get(`/files/${co.data.logo_file_id}`, { responseType: "blob" });
          setLogoDataUrl(await new Promise((resolve) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.readAsDataURL(res.data);
          }));
        } catch {
          setLogoDataUrl("/logo-convic.jpg");
        }
      } else {
        setLogoDataUrl("/logo-convic.jpg");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  }, [weekStart, weekEnd]);

  // Initial + week-change reload
  useEffect(() => {
    reload();
  }, [reload]);

  // Auto-reload on backend sync events
  useSyncWatch(["schedule", "projects", "regii", "vacations", "persons"], () => {
    reload();
  });

  return { data, projects, regii, company, holidays, logoDataUrl, loading, reload };
}
